import { Loader2, LogIn, Palette, Rocket, ShieldCheck, Type } from "lucide-react";

import type { UserProfile } from "@/data/users-api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/** What brand membership grants — spelled out so the owner confirms informed. */
const GRANTS = [
  { icon: LogIn, label: (brand: string) => <>Sign in to the Partner Portal as <b>{brand}</b></> },
  { icon: Palette, label: () => <>Edit your themes, colors, and logo</> },
  { icon: Type, label: () => <>Change your app&rsquo;s terminology</> },
  { icon: Rocket, label: () => <>Publish those changes to your live app</> },
];

/**
 * Step 2 of adding a member: after the owner has verified the profile preview,
 * this dialog spells out exactly what access they're granting before the
 * mutation runs. `user` is the verified profile; null keeps the dialog closed.
 */
export function ConfirmAddDialog({
  user,
  brandName,
  adding,
  onConfirm,
  onOpenChange,
}: {
  user: UserProfile | null;
  brandName: string;
  adding: boolean;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}) {
  if (!user) return null;
  const firstName = user.displayName.split(" ")[0] || `@${user.username}`;

  return (
    <Dialog open onOpenChange={(open) => !adding && onOpenChange(open)}>
      <DialogContent className="rounded-2xl border-border-strong bg-popover shadow-portal sm:max-w-[480px]">
        <DialogHeader>
          <div>
            <span className="rounded-full border border-border-strong bg-panel-2 px-2.5 py-1 text-[10px] font-extrabold tracking-[.07em] text-text-3">
              STEP 2 · CONFIRM ACCESS
            </span>
          </div>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="size-[19px] shrink-0 text-brand" />
            Add {firstName} to {brandName}?
          </DialogTitle>
          <DialogDescription>
            Once you add{" "}
            <b className="font-mono text-[12px] text-brand">@{user.username}</b> to your brand,
            they&rsquo;ll be able to:
          </DialogDescription>
        </DialogHeader>

        <ul className="m-0 list-none p-0">
          {GRANTS.map(({ icon: Icon, label }, i) => (
            <li
              key={i}
              className="flex items-start gap-2.5 border-border py-2 text-[13px] [&+li]:border-t"
            >
              <span className="mt-px grid size-6 shrink-0 place-items-center rounded-lg bg-[color-mix(in_oklab,var(--brand)_12%,transparent)] text-brand">
                <Icon className="size-[13px]" />
              </span>
              <span>{label(brandName)}</span>
            </li>
          ))}
        </ul>

        <div className="rounded-[10px] border border-border bg-panel-2 px-3 py-2.5 text-[12px] text-text-3">
          <b className="text-muted-foreground">They won&rsquo;t be able to</b> invite or remove
          members, or delete the brand. You can remove them from {brandName} at any time.
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" disabled={adding} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" disabled={adding} onClick={onConfirm}>
            {adding && <Loader2 className="size-4 animate-spin" />}
            {adding ? "Adding…" : `Yes, add @${user.username}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
